import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import {
  LayoutDashboard, Utensils, Bot, User, Users, Building2,
  Settings, LogOut, Menu, X, ChevronRight, ClipboardList, Dumbbell, Ruler, Star, Bell, ChefHat,
  ArrowLeft, CalendarRange,
} from 'lucide-react'
import foodLogoMark from '../iconosFoodPlan/food-logo-mark.png'
import { RootState } from '../store/store'
import { useFeatures } from '../hooks/useFeature'
import { api } from '../services/api'
import { logout } from '../store/authSlice'
import AdminNotificationsBell from './AdminNotificationsBell'
import { foodReturnToEcosystemHref, isFoodEmbeddedShell } from '../utils/shellConsumerLoginUrl'

const ALL_USER_NAV = [
  { href: '/dashboard',    label: 'Dashboard',       icon: 'LayoutDashboard', feature: null },
  { href: '/nutrition',    label: 'Nutrición',        icon: 'Utensils',        feature: 'food_log' },
  { href: '/chatbot',      label: 'Chat Nico',          icon: 'Bot',             feature: 'chatbot' },
  { href: '/my-plan',      label: 'Mi Plan',          icon: 'ClipboardList',   feature: 'personal_nutrition' },
  { href: '/measurements', label: 'Medidas',          icon: 'Ruler',           feature: 'measurements' },
  { href: '/daily-report', label: 'Reporte Diario',   icon: 'ClipboardList',   feature: 'daily_report' },
  { href: '/recipes',      label: 'Recetas del Chef', icon: 'ChefHat',            feature: 'recipes' },
  { href: '/local-meals',  label: 'Comidas locales',  icon: 'Utensils',           feature: 'food_log' },
  { href: '/trainers',     label: 'Mi Entrenador',    icon: 'Dumbbell',         feature: 'choose_trainer' },
  { href: '/profile',      label: 'Perfil',           icon: 'User',            feature: null },
]

const ICON_MAP: Record<string, any> = {
  LayoutDashboard, Utensils, Bot, ClipboardList, Ruler, Star, ChefHat, User, Users, Building2, Settings, Bell, Dumbbell, CalendarRange,
}

const navByRole: Record<string, { href: string; label: string; icon: any }[]> = {
  USER: ALL_USER_NAV.map(n => ({ ...n, icon: ICON_MAP[n.icon] })),
  TRAINER: [
    { href: '/trainer', label: 'Panel',  icon: Users },
    { href: '/superadmin/comidas-locales', label: 'Comidas locales', icon: Utensils },
    { href: '/admin/vigencias', label: 'Vigencias', icon: CalendarRange },
    { href: '/trainer/notes-inbox', label: 'Notas', icon: Bell },
    { href: '/trainer/measurement-reminder', label: 'Recordatorio Medidas', icon: Ruler },
    { href: '/profile', label: 'Perfil', icon: User },
  ],
  ADMIN: [
    { href: '/admin',       label: 'Dashboard', icon: LayoutDashboard },
    { href: '/admin/users', label: 'Usuarios',  icon: Users },
    { href: '/admin/vigencias', label: 'Vigencias', icon: CalendarRange },
    { href: '/admin/gyms',  label: 'Gimnasios', icon: Building2 },
    { href: '/superadmin/comidas-locales', label: 'Comidas locales', icon: Utensils },
    { href: '/profile',     label: 'Perfil',    icon: User },
  ],
  SUPER_ADMIN: [
    { href: '/superadmin',        label: 'Dashboard', icon: LayoutDashboard },
    { href: '/admin/users',       label: 'Usuarios',  icon: Users },
    { href: '/admin/vigencias',   label: 'Vigencias', icon: CalendarRange },
    { href: '/superadmin/gyms',   label: 'Gimnasios', icon: Building2 },
    { href: '/superadmin/plans',  label: 'Config',    icon: Settings },
    { href: '/superadmin/recipes',label: 'Recetas',   icon: ChefHat },
    { href: '/superadmin/comidas-locales', label: 'Comidas locales', icon: Utensils },
    { href: '/superadmin/foods',  label: 'Alimentos', icon: Star },
    { href: '/profile',           label: 'Perfil',    icon: User },
  ],
}

const roleLabels: Record<string, string> = {
  USER: 'Usuario', TRAINER: 'Entrenador', ADMIN: 'Admin', SUPER_ADMIN: 'Super Admin',
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { user } = useSelector((s: RootState) => s.auth)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const features = useFeatures()
  const hasFeature = (f: string) => features.includes('all') || features.includes(f)
  const [unreadNotes, setUnreadNotes] = useState(0)
  const [trainerInboxUnread, setTrainerInboxUnread] = useState(0)

  useEffect(() => {
    if (user?.role === 'USER') {
      api.get('/trainer/notes/unread-count')
        .then(r => setUnreadNotes(r.data.unread || 0))
        .catch(() => {})
    }
    if (user?.role === 'TRAINER') {
      const poll = () => {
        api.get('/trainer/notes/inbox-unread-count')
          .then(r => setTrainerInboxUnread(r.data.unread || 0))
          .catch(() => {})
      }
      poll()
      const id = window.setInterval(poll, 60000)
      return () => window.clearInterval(id)
    }
  }, [user, location.pathname])

  const embeddedShell = isFoodEmbeddedShell()
  const inIframe = typeof window !== 'undefined' && window.self !== window.top
  const coachTracking = typeof sessionStorage !== 'undefined'
    && sessionStorage.getItem('d28d_food_coach_tracking') === '1'
  /** Embed densifica chrome; Opción A: NO hereda marca blanca gym/coach. */
  const embeddedCompact = embeddedShell && (inIframe || coachTracking)
  try { localStorage.removeItem('shellBranding') } catch { /* noop */ }
  const rawNavItems = navByRole[user?.role || 'USER'] || []
  // Embed: menú paridad foodplan ADVANCED (sin Mi Entrenador / gym).
  const embedHideTrainerCatalog = embeddedShell
  const navItems = (user?.role === 'USER' && features.length > 0
    ? rawNavItems.filter((item: any) => !item.feature || hasFeature(item.feature))
    : rawNavItems
  ).filter((item: any) => {
    if (!embedHideTrainerCatalog) return true
    return item.feature !== 'choose_trainer' && item.feature !== 'join_gym'
  })
  const isActive = (href: string) => {
    if (href === '/trainer') return location.pathname === '/trainer'
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }
  const canReturnToEcosystem = (() => {
    try {
      if (!embeddedShell) return true
      if (sessionStorage.getItem('shell_can_return_ecosystem') === '1') return true
      if (coachTracking && Boolean(localStorage.getItem('d28d_token'))) return true
      return false
    } catch {
      return !embeddedShell
    }
  })()
  /** Embed: Inicio del shell. Standalone foodplan: `/` es el USER de Food — hay que ir al origen ecosistema. */
  const leaveFoodModule = () => {
    try {
      localStorage.removeItem('d28d_shell')
      localStorage.removeItem('d28d_shell_label')
      localStorage.removeItem('shellBranding')
      sessionStorage.setItem('shell_skip_food_boot', '1')
      sessionStorage.removeItem('shell_can_return_ecosystem')
      sessionStorage.setItem('shell_nav_view', 'home')
    } catch { /* noop */ }
    const href = foodReturnToEcosystemHref()
    if (inIframe && window.top) {
      window.top.location.href = href === '/' ? '/' : href
      return
    }
    window.location.href = href
  }
  const handleLogout = () => {
    dispatch(logout())
    try {
      localStorage.removeItem('d28d_shell')
      localStorage.removeItem('d28d_shell_label')
      localStorage.removeItem('d28d_token')
      localStorage.removeItem('token')
      localStorage.removeItem('refreshToken')
      sessionStorage.removeItem('d28d_food_handoff')
      sessionStorage.removeItem('d28d_food_dest')
      sessionStorage.removeItem('post_payment_boot')
      sessionStorage.removeItem('shell_can_return_ecosystem')
      sessionStorage.removeItem('shell_nav_view')
    } catch { /* noop */ }
    if (embeddedShell) {
      // Cerrar sesión del ecosistema completo (no solo salir del módulo Food).
      window.location.href = '/login?force_logout=1'
      return
    }
    navigate('/login')
  }

  /** Opción A: chrome siempre FOOD PLAN (logo mark + verde #64ba30). */
  const sidebarStyle = {
    background: '#64ba30',
  }

  return (
    <div className={`flex h-screen bg-gray-50 overflow-hidden food-app-shell ${embeddedCompact ? 'food-embedded-compact' : ''}`}>

      {/* ── DESKTOP SIDEBAR (mismo chrome que Food standalone / prod) ── */}
      <aside
        className={`food-sidebar hidden md:flex flex-col flex-shrink-0 ${embeddedCompact ? 'w-52' : 'w-60'}`}
        style={sidebarStyle}
      >

        {/* Logo oficial FOOD PLAN (food-logo-mark + verde menú; sin WL tenant) */}
        <div className={`border-b border-white/20 ${embeddedCompact ? 'px-2.5 py-2' : 'px-3 py-3'}`}>
          <div className="rounded-xl overflow-hidden">
            <img
              src={foodLogoMark}
              alt="Food Plan"
              className="w-full h-auto max-h-[72px] object-contain object-left"
              style={{ display: 'block' }}
            />
          </div>
        </div>

        {/* User */}
        {user && (
          <div className="mx-3 mt-4 mb-2 p-3 rounded-xl bg-white/10 border border-white/20">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {user.firstName?.[0]}{user.lastName?.[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="food-sidebar-user text-white font-medium text-sm truncate">{user.firstName} {user.lastName}</p>
                <span className="food-sidebar-muted text-xs text-white/70">{roleLabels[user.role]}</span>
              </div>
              {/* Bell notification for USER role */}
              {user.role === 'USER' && (
                <Link to="/trainer-notes" onClick={() => setUnreadNotes(0)}
                  className="food-sidebar-nav relative flex-shrink-0 p-1 text-white/70 hover:text-white transition-colors">
                  <Bell size={18} />
                  {unreadNotes > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold">
                      {unreadNotes > 9 ? '9+' : unreadNotes}
                    </span>
                  )}
                </Link>
              )}
              {(user.role === 'SUPER_ADMIN' || user.role === 'ADMIN' || user.role === 'TRAINER') && (
                <AdminNotificationsBell />
              )}
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
          {navItems.map(({ href, label, icon: Icon }) => (
            <Link key={href} to={href}
              className={`food-sidebar-nav flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all text-sm ${
                isActive(href)
                  ? 'food-sidebar-nav--active bg-white text-primary-700 font-semibold shadow-sm'
                  : 'text-white/90 hover:bg-white/15 hover:text-white'
              }`}>
              <Icon size={18} className="flex-shrink-0" />
              <span>{label}</span>
              {user?.role === 'TRAINER' && href === '/trainer/notes-inbox' && trainerInboxUnread > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs min-w-[1.25rem] h-5 px-1 rounded-full flex items-center justify-center font-bold">
                  {trainerInboxUnread > 9 ? '9+' : trainerInboxUnread}
                </span>
              )}
              {isActive(href) && !(user?.role === 'TRAINER' && href === '/trainer/notes-inbox' && trainerInboxUnread > 0) && (
                <ChevronRight size={14} className="ml-auto" />
              )}
            </Link>
          ))}
        </nav>

        <div className="p-2 border-t border-white/20 space-y-0.5">
          {canReturnToEcosystem ? (
            <button type="button" onClick={leaveFoodModule}
              className="food-sidebar-nav flex items-center gap-3 px-3 py-2.5 rounded-xl text-white bg-white/20 hover:bg-white/30 w-full text-sm font-semibold">
              <ArrowLeft size={18} />
              <span>Volver al ecosistema</span>
            </button>
          ) : null}
          <button type="button" onClick={handleLogout}
            className="food-sidebar-nav flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/90 hover:bg-white/15 hover:text-white w-full text-sm">
            <LogOut size={18} />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </aside>

      {/* ── MOBILE OVERLAY SIDEBAR ──────────────────────── */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside
            className="food-sidebar relative w-72 flex flex-col z-50"
            style={sidebarStyle}
          >

            <div className="flex items-center justify-between p-4 border-b border-white/20">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="rounded-xl overflow-hidden w-full max-w-[200px]">
                  <img
                    src={foodLogoMark}
                    alt="Food Plan"
                    className="w-full h-auto object-contain object-left"
                    style={{ display: 'block' }}
                  />
                </div>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="food-sidebar-nav text-white p-1 flex-shrink-0">
                <X size={22} />
              </button>
            </div>

            {user && (
              <div className="mx-3 mt-4 mb-2 p-3 rounded-xl bg-white/10 border border-white/20">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold">
                    {user.firstName?.[0]}{user.lastName?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="food-sidebar-user text-white font-medium text-sm truncate">{user.firstName} {user.lastName}</p>
                    <span className="food-sidebar-muted text-xs text-white/70">{roleLabels[user.role]}</span>
                  </div>
                  {user.role === 'USER' && (
                    <Link to="/trainer-notes" onClick={() => { setSidebarOpen(false); setUnreadNotes(0) }}
                      className="food-sidebar-nav relative flex-shrink-0 p-1 text-white/70 hover:text-white">
                      <Bell size={18} />
                      {unreadNotes > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold">
                          {unreadNotes > 9 ? '9+' : unreadNotes}
                        </span>
                      )}
                    </Link>
                  )}
                  {(user.role === 'SUPER_ADMIN' || user.role === 'ADMIN' || user.role === 'TRAINER') && (
                    <AdminNotificationsBell />
                  )}
                </div>
              </div>
            )}

            <nav className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto">
              {navItems.map(({ href, label, icon: Icon }) => (
                <Link key={href} to={href} onClick={() => setSidebarOpen(false)}
                  className={`food-sidebar-nav flex items-center gap-3 px-4 py-3 rounded-xl text-sm ${
                    isActive(href)
                      ? 'food-sidebar-nav--active bg-white text-primary-700 font-semibold'
                      : 'text-white/90 hover:bg-white/15 hover:text-white'
                  }`}>
                  <Icon size={20} />
                  <span>{label}</span>
                  {user?.role === 'TRAINER' && href === '/trainer/notes-inbox' && trainerInboxUnread > 0 && (
                    <span className="ml-auto bg-red-500 text-white text-xs min-w-[1.25rem] h-5 px-1 rounded-full flex items-center justify-center font-bold">
                      {trainerInboxUnread > 9 ? '9+' : trainerInboxUnread}
                    </span>
                  )}
                </Link>
              ))}
            </nav>

            <div className="p-2 border-t border-white/20 space-y-0.5">
              {canReturnToEcosystem ? (
                <button type="button" onClick={() => { setSidebarOpen(false); leaveFoodModule() }}
                  className="food-sidebar-nav flex items-center gap-3 px-4 py-3 rounded-xl text-white bg-white/20 hover:bg-white/30 w-full text-sm font-semibold">
                  <ArrowLeft size={20} />
                  <span>Volver al ecosistema</span>
                </button>
              ) : null}
              <button type="button" onClick={handleLogout}
                className="food-sidebar-nav flex items-center gap-3 px-4 py-3 rounded-xl text-white/90 hover:bg-white/15 w-full text-sm">
                <LogOut size={20} />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* ── MAIN CONTENT ────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Mobile top bar */}
        <header className="food-main-chrome md:hidden flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl text-gray-600 hover:bg-gray-100">
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-2">
            <img src={foodLogoMark} alt="Food Plan" width={28} height={28} style={{ objectFit: 'contain', display: 'block' }} />
          </div>
<div className="flex items-center gap-2">
            {user?.role === 'USER' && (
              <Link to="/trainer-notes" onClick={() => setUnreadNotes(0)}
                className="relative p-1.5 text-gray-600 hover:text-gray-800">
                <Bell size={20} />
                {unreadNotes > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center font-bold">
                    {unreadNotes > 9 ? '9+' : unreadNotes}
                  </span>
                )}
              </Link>
            )}
            {(user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN' || user?.role === 'TRAINER') && (
              <AdminNotificationsBell compact />
            )}
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
              style={{ background: '#64ba30' }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className={`food-main-content flex-1 overflow-auto bg-gray-50 pb-20 md:pb-0 ${embeddedCompact ? 'food-main-dense' : ''}`}>
          {children}
        </main>

        {/* Mobile bottom nav */}
        <nav className="food-main-chrome md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex z-30"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {navItems.slice(0, 5).map(({ href, label, icon: Icon }) => (
            <Link key={href} to={href}
              className={`flex-1 flex flex-col items-center py-2 gap-0.5 transition-colors ${
                isActive(href) ? 'text-primary-600' : 'text-gray-400'
              }`}>
              <Icon size={20} />
              <span className="text-[10px] font-medium leading-tight">{label}</span>
            </Link>
          ))}
        </nav>
      </div>
    </div>
  )
}
